import { Card, Title, Text, AreaChart } from "@tremor/react";
import { ShieldAlert, Server, Activity, Crosshair, ScrollText, ArrowRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import {
  useDashboardKpis, useAlertTrend, useLatestAudit,
  useFindings, useFindingsSummary,
  type Finding,
} from "@/lib/queries";
import { AlertHeatmap } from "@/components/AlertHeatmap";
import { FindingSourceBadge } from "@/components/FindingSourceBadge";
import { cn } from "@/lib/utils";

type KpiTone = "red" | "blue" | "emerald" | "amber";
type DeltaTone = "up" | "down" | "flat";

const TONE_STYLES: Record<KpiTone, { iconBg: string; iconFg: string }> = {
  red:     { iconBg: "bg-red-500/10",     iconFg: "text-red-600 dark:text-red-400" },
  blue:    { iconBg: "bg-blue-500/10",    iconFg: "text-blue-600 dark:text-blue-400" },
  emerald: { iconBg: "bg-emerald-500/10", iconFg: "text-emerald-600 dark:text-emerald-400" },
  amber:   { iconBg: "bg-amber-500/10",   iconFg: "text-amber-600 dark:text-amber-400" },
};

const DELTA_STYLES: Record<DeltaTone, string> = {
  up:   "text-emerald-600 dark:text-emerald-400",
  down: "text-red-600 dark:text-red-400",
  flat: "text-muted-foreground",
};

function deltaText(now: number, prev: number, label: string): { text: string; tone: DeltaTone } {
  if (prev === 0 && now === 0) return { text: "→ no change", tone: "flat" };
  if (prev === 0)               return { text: `↑ ${now} new ${label}`, tone: "up" };
  const diff = now - prev;
  if (diff === 0)               return { text: "→ no change", tone: "flat" };
  const pct = Math.round((Math.abs(diff) / prev) * 100);
  if (diff > 0) return { text: `↑ ${pct}% vs prev 24h`, tone: "up" };
  return { text: `↓ ${pct}% vs prev 24h`, tone: "down" };
}

interface KpiProps {
  label: string;
  value: string;
  delta?: { text: string; tone: DeltaTone };
  icon: LucideIcon;
  tone: KpiTone;
  loading?: boolean;
}

function Kpi({ label, value, delta, icon: Icon, tone, loading }: KpiProps) {
  const t = TONE_STYLES[tone];
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">{label}</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">
            {loading ? <span className="inline-block h-8 w-16 animate-pulse rounded bg-muted" /> : value}
          </div>
          {delta && !loading && (
            <div className={`mt-1 text-xs font-medium ${DELTA_STYLES[delta.tone]}`}>
              {delta.text}
            </div>
          )}
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${t.iconBg}`}>
          <Icon className={`h-5 w-5 ${t.iconFg}`} />
        </div>
      </div>
    </Card>
  );
}

function fmtRunAt(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const SEVERITY_BORDER: Record<string, string> = {
  critical: "border-l-red-500",
  high:     "border-l-orange-500",
  medium:   "border-l-amber-500",
  low:      "border-l-emerald-500",
  info:     "border-l-blue-500",
};

const SEVERITY_TEXT: Record<string, string> = {
  critical: "text-red-600 dark:text-red-400",
  high:     "text-orange-600 dark:text-orange-400",
  medium:   "text-amber-600 dark:text-amber-400",
  low:      "text-emerald-600 dark:text-emerald-400",
  info:     "text-blue-600 dark:text-blue-400",
};

function FindingCard({ finding }: { finding: Finding }) {
  return (
    <Link
      to={`/findings?id=${finding.id}`}
      className={cn(
        "block rounded-md border-l-4 border-y border-r border-border bg-card px-4 py-3 transition-colors hover:bg-muted/40",
        SEVERITY_BORDER[finding.severity],
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn("text-[10px] font-semibold uppercase tracking-wider", SEVERITY_TEXT[finding.severity])}>
              {finding.severity}
            </span>
            <FindingSourceBadge source={finding.source} size="xs" />
            {finding.category && (
              <span className="font-mono text-[10px] text-muted-foreground">{finding.category}</span>
            )}
          </div>
          <div className="mt-1 text-sm font-medium leading-snug">{finding.title}</div>
          {(finding.affected_hosts.length > 0 || finding.affected_actors.length > 0) && (
            <div className="mt-1 text-xs text-muted-foreground">
              {finding.affected_hosts.length > 0 && (
                <span>{finding.affected_hosts.slice(0, 3).join(", ")}{finding.affected_hosts.length > 3 ? ` +${finding.affected_hosts.length - 3}` : ""}</span>
              )}
              {finding.affected_hosts.length > 0 && finding.affected_actors.length > 0 && <span> · </span>}
              {finding.affected_actors.length > 0 && (
                <span className="font-mono">{finding.affected_actors.slice(0, 2).join(", ")}{finding.affected_actors.length > 2 ? ` +${finding.affected_actors.length - 2}` : ""}</span>
              )}
            </div>
          )}
        </div>
        <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const kpis = useDashboardKpis();
  const trend = useAlertTrend(14);
  const audit = useLatestAudit();
  const findingsSummary = useFindingsSummary();
  // Most recent open findings — across all audits, highest severity first.
  const openFindings = useFindings({ status: "open", limit: 6 });

  const k = kpis.data;
  const fs = findingsSummary.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          What Tyrael decided. Top of page is what needs your attention right
          now; numbers and charts below are the supporting data.
        </p>
      </div>

      {/* Open findings — the primary work surface */}
      <Card>
        <div className="flex items-start justify-between gap-3 border-b border-border pb-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
              <ScrollText className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Open findings</div>
              <div className="text-base font-semibold">
                {fs ? `${fs.total_open} need attention` : "Loading…"}
                {fs && fs.open_by_severity.critical > 0 && (
                  <span className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-600 dark:text-red-400">
                    {fs.open_by_severity.critical} critical
                  </span>
                )}
              </div>
            </div>
          </div>
          <Link
            to="/findings"
            className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            All findings →
          </Link>
        </div>

        <div className="mt-4 space-y-2">
          {openFindings.isLoading && (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
          {openFindings.data?.items.length === 0 && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-400">
              All clear — no open findings right now.
            </div>
          )}
          {openFindings.data?.items.map((f) => (
            <FindingCard key={f.id} finding={f} />
          ))}
        </div>

        {audit.data && (
          <div className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
            Latest audit: <Link to="/audits" className="text-primary hover:underline">#{audit.data.id}</Link> ·
            {" "}{fmtRunAt(audit.data.run_at)}
          </div>
        )}
      </Card>

      {/* KPI strip */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Kpi
          label="Critical alerts (24h)"
          value={String(k?.critical_alerts_24h ?? 0)}
          delta={k ? deltaText(k.critical_alerts_24h, k.critical_alerts_prev_24h, "today") : undefined}
          icon={ShieldAlert}
          tone="red"
          loading={kpis.isLoading}
        />
        <Kpi
          label="Active hosts (5m)"
          value={String(k?.active_hosts ?? 0)}
          icon={Server}
          tone="blue"
          loading={kpis.isLoading}
        />
        <Kpi
          label="Flows / sec"
          value={String(k?.flows_per_sec ?? 0)}
          icon={Activity}
          tone="emerald"
          loading={kpis.isLoading}
        />
        <Kpi
          label="Threat-intel hits (24h)"
          value={String(k?.threat_intel_24h ?? 0)}
          delta={k ? deltaText(k.threat_intel_24h, k.threat_intel_prev_24h, "today") : undefined}
          icon={Crosshair}
          tone="amber"
          loading={kpis.isLoading}
        />
      </div>

      <Card>
        <Title>Alert volume — last 14 days</Title>
        <Text>Bucketed by severity. Source: <code>flow_alerts</code>.</Text>
        <AreaChart
          className="mt-4 h-72"
          data={trend.data ?? []}
          index="date"
          categories={["critical", "high", "medium"]}
          colors={["red", "amber", "yellow"]}
          showAnimation
          curveType="monotone"
        />
      </Card>

      <AlertHeatmap />
    </div>
  );
}
